import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Land-Based Empires (c. 1450 – c. 1750)",
  examWeight: "8–10%",
  focus: "How did large, centralized 'Gunpowder Empires' use military technology, bureaucracy, and culture to expand and maintain control?",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Empires Expand",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💥 The Gunpowder Revolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The term "Gunpowder Empires" refers to large, multiethnic states in Southwest, Central, and South Asia that relied on firearms to conquer and control territories.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🔫 The Technology</h4>
                <p className="text-sm text-slate-700 mt-1">The spread of gunpowder (originally from China) via the Silk Roads allowed states to use artillery (cannons) to destroy fortification walls and muskets to dominate traditional cavalry.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏰 Centralization</h4>
                <p className="text-sm text-slate-700 mt-1">Gunpowder weapons were expensive; only wealthy, centralized states could afford standing armies equipped with them, leading to the decline of decentralized feudal lords.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕌 The Islamic Gunpowder Empires</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These three empires emerged from the vacuum left by the Mongol Khanates.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇹🇷 The Ottoman Empire</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Rise:</strong> Founded by Osman; expanded by Mehmed II ("The Conqueror").</p>
                <p className="text-sm text-slate-700 mt-1"><strong>1453 Conquest of Constantinople:</strong> Mehmed II used massive cannons to breach the Byzantine walls. Constantinople was renamed Istanbul, becoming a nexus of trade between East and West.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Peak (Suleiman I, r. 1520–1566):</strong> Expanded deep into Europe (Siege of Vienna, 1529), reformed the legal code, and reached the height of power.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Decline:</strong> Slow decline starting after 1600 due to weak sultans and strong European neighbors (the "Sick Man of Europe").</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇮🇷 The Safavid Empire</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Origins:</strong> Established by Ismail (a Sufi commander) who conquered most of Persia at age 14 or 15.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Religion as Power:</strong> Ismail proclaimed Twelver Shi'a Islam the official religion, creating a distinct Persian identity but causing deep conflict with the Sunni Ottomans.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Conflicts:</strong> Frequent warfare with the Ottomans (Battle of Chaldiran, 1514). The Ottomans had artillery; the Safavids did not (initially), leading to Safavid defeat.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Peak (Shah Abbas I, r. 1588–1629):</strong> Modernized the army with European help and beautified the capital, Isfahan.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">🇮🇳 The Mughal Empire</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Rise:</strong> Founded by Babur (descendant of Tamerlane and Genghis Khan) who invaded northern India in the 1520s using gunpowder.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Akbar the Great (r. 1556–1605):</strong> Created a centralized government with a fair tax code. Known for religious tolerance: he was Muslim ruling a Hindu majority, married Hindu wives, and removed the jizya (tax on non-Muslims).</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Decline (Aurangzeb, r. 1658–1707):</strong> Expanded the empire south but drained the treasury and ended religious tolerance, sparking rebellions among Hindus and Sikhs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏯 East Asia: The Manchu (Qing Dynasty)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📉 The Ming Decline</h4>
                <p className="text-sm text-slate-700 mt-1">The Ming Dynasty (native Han Chinese) declined due to corruption, famine, and peasant revolts.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">👑 The Qing (1644–1911)</h4>
                <p className="text-sm text-slate-700 mt-1">The Manchu people from Manchuria (northeast of the Great Wall) seized Beijing and established the Qing Dynasty.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🗺️ Expansion</h4>
                <p className="text-sm text-slate-700 mt-1">Under Emperor Kangxi and Emperor Qianlong, China expanded into Taiwan, Mongolia, Central Asia, and Tibet.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🎓 Identity</h4>
                <p className="text-sm text-slate-700 mt-1">The Manchus maintained their ethnic distinctiveness (forbidding intermarriage with Han Chinese) but utilized the traditional Chinese Civil Service Exam and bureaucracy to rule.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇷🇺 Russia: Expansion and Westernization</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">⚔️ Ivan IV ("The Terrible")</h4>
                <p className="text-sm text-slate-700 mt-1">Expanded Russian borders eastward into Siberia using gunpowder and Cossacks (peasant warriors) to fight local tribes and the Siberian Khanate. Motivation: The Fur Trade (expanding East to the Pacific).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">👑 The Romanov Dynasty & Peter the Great (r. 1682–1725)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Westernization:</strong> Peter visited Western Europe and modernized the military and government based on European models.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>St. Petersburg:</strong> Moved the capital from Moscow to St. Petersburg (his "Window on the West") to access the Baltic Sea.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Europe and Africa</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-slate-100 to-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">🇪🇺 Europe</h4>
                <p className="text-sm text-slate-700 mt-1">Monarchies consolidated power (New Monarchies) in France, Spain, and England. Focus on bureaucracy and professional armies.</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌍 Africa</h4>
                <p className="text-sm text-slate-700 mt-1">The Songhai Empire (West Africa) crumbled after being attacked by Morocco in 1591. Morocco used gunpowder (muskets); Songhai did not.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Empires: Administration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Centralizing Bureaucracy and Military</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Rulers needed to strip power from the nobility and concentrate it in the capital.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇹🇷 Ottoman Devshirme System</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>"Blood Tax":</strong> Boys aged 8–20 were taken from Christian communities in the Balkans, converted to Islam, and educated.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Janissaries:</strong> Elite soldiers loyal only to the Sultan (not to local tribes).</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Bureaucrats:</strong> The smartest boys became scribes, tax collectors, and diplomats.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🗾 Japanese Samurai (Tokugawa Shogunate)</h4>
                <p className="text-sm text-slate-700 mt-1">After the "Warring States" period, the Tokugawa Shoguns unified Japan.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Alternate Attendance Policy:</strong> The Daimyo (landowning nobles) had to maintain two homes (one in their domain, one in the capital Edo/Tokyo). If the Daimyo left Edo, his family had to stay behind as hostages. This reduced the Daimyo's ability to rebel.</p>
                <p className="text-sm text-slate-700 mt-1">Samurai became salaried bureaucrats rather than independent warriors.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🇷🇺 Russian Oprichnina</h4>
                <p className="text-sm text-slate-700 mt-1">Ivan IV created a paramilitary force loyal only to him to crush the Boyars (Russian nobility).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎨 Religious and Cultural Legitimation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Rulers used religion, art, and architecture to display power and convince subjects of their right to rule.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">👑 Divine Right (Europe)</h4>
                <p className="text-sm text-slate-700 mt-1">Kings (like Louis XIV of France) claimed they ruled because God chose them. "I am the State."</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">☪️ Shi'a Islam (Safavid)</h4>
                <p className="text-sm text-slate-700 mt-1">Rulers claimed descent from the Prophet Muhammad's family to validate rule.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🔪 Aztec/Mexica (Americas)</h4>
                <p className="text-sm text-slate-700 mt-1">Human sacrifice rituals performed in public demonstrated the ruler's power and religious necessity.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🕌 Songhai (Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Askia the Great promoted Islam and made a pilgrimage to Mecca to legitimize his rule over a Muslim trading network.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Art and Architecture</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-900">🕌 Taj Mahal (Mughal)</h5>
                <p className="text-xs text-slate-700 mt-1">Built by Shah Jahan to show wealth and power.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-900">🕌 Suleymaniye Mosque (Ottoman)</h5>
                <p className="text-xs text-slate-700 mt-1">Istanbul mosque that projected Islamic authority.</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h5 className="font-bold text-amber-900">🏰 Palace of Versailles (France)</h5>
                <p className="text-xs text-slate-700 mt-1">Louis XIV's palace used to house nobles and display immense wealth.</p>
              </div>
              <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-900">👑 Imperial Portraits (Qing)</h5>
                <p className="text-xs text-slate-700 mt-1">Legitimized Manchu emperors by depicting them in traditional Chinese scholarly garb.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Taxation Systems</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Expansion required money. Empires developed complex tax collection methods.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🏛️ Tax Farming (Ottomans & Mughals)</h4>
                <p className="text-sm text-slate-700 mt-1">The government auctioned the right to collect taxes to private individuals (Tax Farmers). The Farmer paid the state a set amount and kept the extra they collected from the peasants.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Issue:</strong> Eventually led to corruption and over-taxation of the peasantry.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">👨‍💼 Zamindars (Mughal)</h4>
                <p className="text-sm text-slate-700 mt-1">Paid government officials in charge of specific duties like taxation, construction, and water supply.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🪙 Single Whip System (Ming/Qing China)</h4>
                <p className="text-sm text-slate-700 mt-1">Required taxes to be paid in hard currency (silver) rather than goods (grain/rice), integrating China into the global silver trade.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Empires: Belief Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛪ Christianity in Europe: The Reformation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Catholic Church faced a massive fracture, ending religious unity in Western Europe.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">✝️ The Protestant Reformation (1517)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Martin Luther:</strong> German monk who posted the 95 Theses.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Grievances:</strong> Sale of indulgences (paying for forgiveness of sins), simony (selling church offices), and corruption.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Core Belief:</strong> "Salvation by Faith Alone" (not deeds) and the Bible is the ultimate authority (not the Pope).</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Impact:</strong> Political fragmentation. Kings (like Henry VIII of England) broke from the Pope to consolidate their own power and seize Church lands.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">⛪ The Counter-Reformation (Catholic)</h4>
                <p className="text-sm text-slate-700 mt-1">The Church's response to stop the spread of Protestantism.</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Jesuits:</strong> Missionary order founded to convert people to Catholicism (active in Americas and Asia).</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Council of Trent:</strong> Corrected Church abuses but reaffirmed rituals and the Pope's authority.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">⚔️ Religious Wars</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Thirty Years' War (1618–1648):</strong> Devastating conflict in the Holy Roman Empire (Germany) between Catholics and Protestants. Ended with the Peace of Westphalia, establishing the concept of state sovereignty over religion.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">☪️ Islam: The Sunni-Shi'a Split Intensifies</h3>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">⚔️ Ottoman (Sunni) vs. Safavid (Shi'a)</h4>
              <p className="text-sm text-slate-700 mt-1">This was not just a territorial war but a religious ideological battle. The Ottoman Sultan claimed the title of Caliph (leader of all Muslims). The Safavid Shah enforced Shi'a Islam to create a unified Persian identity distinct from the Turkic Ottomans.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇮🇳 South Asia: Sikhism</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">📖 Context</h4>
                <p className="text-sm text-slate-700 mt-1">Interaction between Hinduism and Islam in Mughal India.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">👨‍🔬 Founding</h4>
                <p className="text-sm text-slate-700 mt-1">Founded by Guru Nanak.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🙏 Beliefs</h4>
                <p className="text-sm text-slate-700 mt-1">Monotheistic (like Islam) but recognized karma and reincarnation (like Hinduism). Rejected the Caste System.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">⚔️ Evolution</h4>
                <p className="text-sm text-slate-700 mt-1">Originally pacifist, Sikhism became militant to defend against Mughal persecution (under Aurangzeb).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Comparison in Land-Based Empires",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Military Comparison</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">✅ Similarity</h4>
                <p className="text-sm text-slate-700 mt-1">All land-based empires (Ottoman, Mughal, Safavid, Qing, Russia) relied on gunpowder weaponry to expand and subjugate nomadic groups.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">❌ Difference</h4>
                <p className="text-sm text-slate-700 mt-1">Some used enslaved soldiers (Ottoman Janissaries, Safavid Ghulams) while others used conscripted citizens or samurai elites.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Administrative Comparison</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">✅ Similarity: Bureaucratic Elites</h4>
                <p className="text-sm text-slate-700 mt-1">Use of bureaucratic elites to manage large populations (Chinese Civil Service, Ottoman Devshirme, French Intendants).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">✅ Similarity: Religious/Cultural Justification</h4>
                <p className="text-sm text-slate-700 mt-1">Use of religion/art to justify rule (Divine Right, Caliphate status, Imperial Portraits).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">❌ Difference: Treatment of Minorities</h4>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-slate-700"><strong>Tolerant:</strong> Akbar (Mughal) and the Ottoman Millet System (allowed Jews/Christians to rule themselves under their own religious laws).</p>
                  <p className="text-sm text-slate-700"><strong>Intolerant:</strong> Spanish expulsion of Jews/Moors; Louis XIV revoking the Edict of Nantes (persecuting Protestants); Qing restricting Han men (Queues hairstyle).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Economic Comparison</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">✅ Similarity</h4>
                <p className="text-sm text-slate-700 mt-1">Focus on tax collection to fund the military.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">❌ Difference: Global Trade</h4>
                <p className="text-sm text-slate-700 mt-1">The Ottomans/Safavids controlled Silk Road terminuses; the Ming/Qing sought to restrict trade (Canton System); Russia sought to force entry into maritime trade (St. Petersburg).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📚 Important Vocabulary for Unit 3</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Bureaucracy" 
                definition="A system of government in which most of the important decisions are made by state officials rather than by elected representatives."
              />
              <KeyTerm 
                term="Devshirme" 
                definition="Ottoman system of taking Christian boys to serve the state."
              />
              <KeyTerm 
                term="Janissaries" 
                definition="Elite Ottoman infantry units; gunpowder experts."
              />
              <KeyTerm 
                term="Zamindars" 
                definition="Mughal tax collectors."
              />
              <KeyTerm 
                term="Samurai" 
                definition="Japanese warrior class."
              />
              <KeyTerm 
                term="Divine Right of Kings" 
                definition="The belief that the authority of kings comes directly from God."
              />
              <KeyTerm 
                term="Absolute Monarchy" 
                definition="A monarchy that is not limited or restrained by laws or a constitution (e.g., Louis XIV)."
              />
              <KeyTerm 
                term="Indulgences" 
                definition="Selling of forgiveness by the Catholic Church."
              />
              <KeyTerm 
                term="Simony" 
                definition="The buying or selling of ecclesiastical privileges (Church jobs)."
              />
              <KeyTerm 
                term="95 Theses" 
                definition="Arguments written by Martin Luther against the Catholic Church."
              />
              <KeyTerm 
                term="Sikhism" 
                definition="Syncretic religion formed in India."
              />
              <KeyTerm 
                term="Versailles" 
                definition="Palace constructed by Louis XIV to reflect his power."
              />
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit3Notes;